// src/pages/admin/PayoutsPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminPayoutsApi } from '../../api/services'
import { Card, CardHeader, CardBody, Button, StatusBadge, PageSpinner } from '../../components/common/ui'
import toast from 'react-hot-toast'

export default function AdminPayoutsPage() {
  const qc = useQueryClient()
  const [genForm, setGenForm] = useState({ supplier_id:'', period_from:'', period_to:'', transfer_mode:'manual' })
  const [markId, setMarkId] = useState(null)
  const [ref, setRef] = useState('')

  const { data, isLoading } = useQuery({ queryKey: ['admin-payouts'], queryFn: () => adminPayoutsApi.index() })
  const generate = useMutation({ mutationFn: () => adminPayoutsApi.generate(genForm), onSuccess: () => { toast.success('Payout generated'); qc.invalidateQueries(['admin-payouts']); setGenForm({ supplier_id:'', period_from:'', period_to:'', transfer_mode:'manual' }) }, onError: e => toast.error(e.response?.data?.message || 'Error') })
  const markPaid = useMutation({ mutationFn: id => adminPayoutsApi.markPaid(id, { reference_number: ref }), onSuccess: () => { toast.success('Marked as paid'); qc.invalidateQueries(['admin-payouts']); setMarkId(null); setRef('') } })

  if (isLoading) return <PageSpinner />
  const { payouts = {}, summary = {}, pending_suppliers = [] } = data?.data?.data || {}
  const list = payouts.data || []

  return (
    <div>
      <h1 className="text-xl font-display font-bold mb-6">Payouts</h1>

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        {[['Pending payout',`₹${Number(summary.total_pending||0).toLocaleString('en-IN')}`],['Paid this month',`₹${Number(summary.paid_this_month||0).toLocaleString('en-IN')}`],['Total paid',`₹${Number(summary.total_paid||0).toLocaleString('en-IN')}`]].map(([l,v])=>(
          <div key={l} className="bg-white border border-border rounded-xl p-4"><p className="text-xs text-muted">{l}</p><p className="text-xl font-bold">{v}</p></div>
        ))}
      </div>

      <Card className="mb-6">
        <CardHeader><span className="font-bold text-sm">Generate Payout</span></CardHeader>
        <CardBody className="space-y-3">
          <div className="grid sm:grid-cols-4 gap-3">
            <input className="input" placeholder="Supplier ID" type="number" value={genForm.supplier_id} onChange={e => setGenForm(f => ({ ...f, supplier_id: e.target.value }))} />
            <input className="input" type="date" value={genForm.period_from} onChange={e => setGenForm(f => ({ ...f, period_from: e.target.value }))} />
            <input className="input" type="date" value={genForm.period_to} onChange={e => setGenForm(f => ({ ...f, period_to: e.target.value }))} />
            <select className="input" value={genForm.transfer_mode} onChange={e => setGenForm(f => ({ ...f, transfer_mode: e.target.value }))}>
              {['manual','neft','imps','rtgs','razorpay_x'].map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <Button size="sm" loading={generate.isPending} disabled={!genForm.supplier_id} onClick={() => generate.mutate()}>Generate Payout</Button>
        </CardBody>
      </Card>

      {pending_suppliers.length > 0 && (
        <Card className="mb-6">
          <CardHeader><span className="font-bold text-sm">Pending Payout Suppliers</span></CardHeader>
          <CardBody className="p-0">
            {pending_suppliers.map(s => (
              <div key={s.supplier_id} className="flex items-center justify-between px-5 py-3 border-b border-border last:border-b-0 text-sm">
                <div><p className="font-bold">{s.supplier_name}</p><p className="text-xs text-muted">{s.business_name} · {s.orders} orders</p></div>
                <span className="font-bold text-pink">₹{Number(s.total_pending).toLocaleString('en-IN')}</span>
              </div>
            ))}
          </CardBody>
        </Card>
      )}

      <div className="space-y-3">
        {list.map(p => (
          <Card key={p.id}>
            <CardBody className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2"><span className="font-bold text-sm">{p.supplier?.business_name || p.supplier?.name}</span><StatusBadge status={p.status} /></div>
                <p className="text-xs text-muted">₹{Number(p.amount || p.net_payout).toLocaleString('en-IN')} · {p.transfer_mode} · {new Date(p.created_at).toLocaleDateString('en-IN')}</p>
              </div>
              {p.status === 'pending' && (
                markId === p.id ? (
                  <div className="flex gap-2">
                    <input className="input" placeholder="Reference #" value={ref} onChange={e => setRef(e.target.value)} />
                    <Button size="sm" loading={markPaid.isPending} onClick={() => markPaid.mutate(p.id)}>Confirm Paid</Button>
                    <Button size="sm" variant="ghost" onClick={() => setMarkId(null)}>Cancel</Button>
                  </div>
                ) : <Button size="sm" variant="secondary" onClick={() => setMarkId(p.id)}>Mark Paid</Button>
              )}
            </CardBody>
          </Card>
        ))}
        {list.length === 0 && <p className="text-sm text-muted text-center py-8">No payouts.</p>}
      </div>
    </div>
  )
}
